'use client';

import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronsRight, Search } from 'lucide-react';
import { useParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import TabSwitch from '../tab-switch';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import UserButton from '@/components/user/user-button';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useRouter } from '@/i18n/routing';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { CITIES, useAiFoodOrderStore, useAuthStore } from '@/store';

export default function SolomealHeader() {
  const params = useParams();
  const router = useRouter();
  const address = useAiFoodOrderStore.use.address();
  const citySlug = Array.isArray(params.city) ? params.city[0] : params.city;
  const cityLabel = CITIES.find((c) => c.slug === citySlug)?.label ?? citySlug;
  const { openCitySheet } = useStoreCitySheet();
  const cityId = useAiFoodOrderStore.use.city_id();
  const { user } = useAuthStore();

  const freeShipValue = useMemo(
    () => (cityId === 'DAD' ? '29k' : '33k'),
    [cityId]
  );

  const isWebInApp = useDetectWebInApp();

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'solomeal',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  return (
    <>
      <NotificationDemo />
      <div className="relative overflow-hidden px-4 pb-6 flex flex-col bg-gradient-to-b from-[#ECF6FF] to-[#76C0FF]">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPosition: 'bottom center',
            backgroundImage: `url(${assetPrefix}/static/icons/solomeal-bg.svg)`,
          }}
        />
        <header className="sticky top-0 z-50 mx-auto flex flex-col h-auto w-full max-w-screen-xl">
          {/* Address section */}
          <div className="py-6">
            <Card
              className={cn(
                'w-full rounded-full bg-white/60 p-2 backdrop-blur-sm cursor-pointer text-left flex gap-4 items-center border border-white transition-colors',
                isWebInApp ? 'justify-start' : 'justify-between pl-4'
              )}
              onClick={() => openCitySheet()}
            >
              {isWebInApp && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    logBackButtonClick();
                    clearDebugLogs();
                    JSFunction.call({ name: 'close' });
                  }}
                  className="flex size-10 items-center justify-center rounded-full bg-white/80 backdrop-blur-sm"
                >
                  <ArrowLeft className="size-6 text-neutral-70" />
                </button>
              )}
              <div className="flex flex-col gap-0 hover:bg-none bg-transparent text-left px-0">
                <span className="text-primary text-lg font-bold flex items-center">
                  Giao món đến
                  <ChevronDown className="ml-2 size-4 text-primary" />
                </span>
                <div className="flex items-center">
                  <span
                    className={cn(
                      'text-neutral-90 text-xs font-bold truncate',
                      isWebInApp
                        ? 'max-w-[calc(100vw-120px)]'
                        : 'max-w-[calc(100vw-150px)]'
                    )}
                  >
                    {address?.address ?? cityLabel}
                  </span>
                </div>
              </div>
              <div
                role="button"
                className="flex items-center"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                tabIndex={0}
              >
                {!isWebInApp ? <UserButton /> : null}
              </div>
            </Card>
          </div>

          {/* Tab Switch */}
          <div className="container mx-auto mb-4 px-0">
            <TabSwitch />
          </div>

          {/* Center content */}
          <div className="w-full flex flex-col justify-center items-center relative">
            <img
              src={`${assetPrefix}/static/images/solomeal-title.webp`}
              alt="solomeal"
              className="max-w-[60vw] h-auto"
            />
            <div className="bg-white rounded-full p-1 -rotate-1 -mt-2">
              <span className="flex items-center text-secondary-70 font-bold justify-center border-dashed border-2 border-neutral-20 rounded-full px-4">
                Bữa ăn {freeShipValue} + Miễn phí vận chuyển
                <ChevronsRight className="ml-1" />
              </span>
            </div>

            <Button
              onClick={() =>
                router.push(`/${citySlug}/danh-sach-cua-hang-solomeal`)
              }
              size="icon"
              variant="secondary"
              className="absolute right-0 top-0 [&_svg]:size-4 [&_svg]:font-bold [&_svg]:text-secondary-70 bg-secondary-20 rounded-full"
            >
              <Search />
            </Button>

            {/* 🍜 Animated dish icons */}
            <DishCarousel />
          </div>
        </header>
      </div>
    </>
  );
}

export function DishCarousel() {
  const items = [
    `${assetPrefix}/static/images/mon-1.webp`,
    `${assetPrefix}/static/images/mon-2.webp`,
    `${assetPrefix}/static/images/mon-3.webp`,
  ];

  // order đại diện vị trí (index của itemId ở các vị trí): ví dụ [0,1,2] nghĩa:
  // vị trí left <- item 0, center <- item 1, right <- item 2
  const [order, setOrder] = useState<number[]>([0, 1, 2]);

  // cấu hình vị trí (px) và scale khi item được đặt vào vị trí đó
  const positions = [
    { x: -140, y: -20, scale: 1, z: 1, opacity: 0.9 }, // left
    { x: 0, y: 20, scale: 1.5, z: 3, opacity: 1 }, // center (lớn nhất)
    { x: 140, y: -30, scale: 0.8, z: 2, opacity: 0.9 }, // right
  ];

  // thời gian animation (s) và interval đổi chỗ (ms)
  const animDuration = 0.6;
  const swapInterval = 3000;

  useEffect(() => {
    const timer = setInterval(() => {
      // rotate theo vòng: [a,b,c] -> [c,a,b]
      setOrder((prev) => [prev[2], prev[0], prev[1]]);
    }, swapInterval);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full h-40 mt-4 flex justify-center items-center overflow-visible">
      {/* container center reference */}
      <div className="absolute size-full left-0 top-0 pointer-events-none" />

      {items.map((src, itemId) => {
        // tìm vị trí hiện tại của item này trong order -> posIndex in [0,1,2]
        const posIndex = order.indexOf(itemId);
        const pos = positions[posIndex];

        return (
          <motion.div
            key={itemId} // key cố định cho mỗi item
            initial={false}
            animate={{
              x: pos.x,
              y: pos.y,
              scale: pos.scale,
              opacity: pos.opacity,
              // zIndex không được animate trực tiếp, set bằng style
            }}
            transition={{ duration: animDuration, ease: 'easeInOut' }}
            style={{ zIndex: pos.z }}
            className="absolute bg-transparent rounded-full overflow-visible"
            aria-hidden={false}
          >
            <img
              src={src}
              alt={`dish-${itemId}`}
              className="size-24 rounded-full object-cover overflow-visible"
              draggable={false}
            />
          </motion.div>
        );
      })}
    </div>
  );
}
